import React from 'react';
import { useIntl } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { CollapseFAQ, H3, LayoutSingleColumn, NamedLink, Page } from '../../components';
import SectionFooter from '../PageBuilder/SectionBuilder/SectionFooter';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import LamiaWithDogsImage from './images/Lamia in the window with 2 dogs - SMALL - HouseSit Match 2023-214.jpg';
import HiringPetsittersImage from './images/Hiring petsitters - Chloe Gillian and Kevan - HouseSit Match 2023-142.jpg'
import BriefingImage from './images/HouseSit Match 2023-207 Dog biscuits - Food.jpg'
import css from './WhatIsHouseSittingPage.module.css';
import { defaultFaq } from './faq';

export const WhatIsHouseSittingPage = props => {
    const config = useConfiguration();
    const intl = useIntl();

    const marketplaceName = config.marketplaceName;
    const schemaTitle = intl.formatMessage({ id: 'WhatIsHouseSittingPage.schemaTitle' }, { marketplaceName });
    const schemaDescription = intl.formatMessage({ id: 'WhatIsHouseSittingPage.schemaDescription' }, { marketplaceName });
    return (
        <Page
            title={schemaTitle}
            description={schemaDescription}
        >
            <LayoutSingleColumn
                topbar={<TopbarContainer />}
                footer={<SectionFooter />}
                mainColumnClassName={css.mainColumnClassName}
            >
                <div className={css.root}>
                    <p className={css.info}>
                        House sitting has become an increasingly popular way for homeowners and travellers to help each other out. Whether you're looking to ensure your home and pets are cared for while you're away or want to explore new destinations without the cost of accommodation, house sitting offers a practical solution.
                    </p>
                    <br />
                    <p className={css.info}>
                        At HouseSit Match, we specialise in connecting property owners and house sitters to create mutually beneficial arrangements that provide peace of mind for property owners and unforgettable experiences for sitters.
                    </p>

                    <H3>
                        What is House Sitting?
                    </H3>
                    <p className={css.info}>
                        House sitting is a temporary arrangement where a trusted individual, the house sitter, takes care of a homeowner's property in their absence. This arrangement often includes tasks such as caring for pets, maintaining gardens, handling light household duties, and providing an overall sense of security for the property.
                    </p>

                    <div className={css.imageContainerVertical}>
                        <img className={css.image} src={LamiaWithDogsImage} alt='what is housesitting' />
                        <p className={css.info}>
                            When asked ‘what is house sitting?’ many people respond it’s about caring for a home and pets in the owner’s absence.
                        </p>
                    </div>
                    <br />
                    <p className={css.info}>
                        Unlike traditional paid services like hiring a pet sitter, a housekeeper or gardener, house sitting is typically based on an exchange. The sitter offers their time and services, and in return, they gain free accommodation—making it an attractive option for both parties.
                    </p>

                    <p className={css.subtitle}>
                        What Is a House Sitter?
                    </p>
                    <p className={css.info}>
                        A house sitter is someone who agrees to live in another person’s home, usually without paying rent, in exchange for performing agreed-upon responsibilities. These responsibilities can vary but often include pet care, watering plants, and ensuring the property remains secure.
                    </p>

                    <p className={css.info}>
                        Many house sitters see this as more than just a practical arrangement; it’s a unique lifestyle opportunity. For frequent travellers, retirees, or remote workers, house sitting allows them to explore different locations without the expense of hotels or rentals.
                    </p>

                    <H3>
                        House Sitting: A Mutually Beneficial Arrangement
                    </H3>

                    <p className={css.info}>
                        House sitting is a mutually rewarding relationship between homeowners and sitters. At its core, house sitting offers owners the assurance that their home, pets, and property will be cared for in their absence. Meanwhile, sitters gain the chance to live rent-free, often in unique locations, making it an appealing option for travellers and remote workers alike.
                    </p>
                    <p className={css.info}>
                        Here are some key benefits of this unique arrangement:
                    </p>

                    <p className={css.subtitle}>
                        Benefits for Homeowners
                    </p>
                    <ul className={css.list}>
                        <li>
                            Peace of Mind: Knowing your property and pets are in trustworthy hands helps alleviate travel stress.
                        </li>
                        <li>
                            Pet Comfort and Care: Pets remain in familiar surroundings, reducing stress and avoiding the expense of kennels or pet hotels.
                        </li>
                        <li>
                            Enhanced Home Security: An occupied home deters break-ins, providing a layer of security that empty properties lack.
                        </li>
                        <li>
                            Cost Savings: Skip costs for pet boarding, garden maintenance, and increased insurance premiums associated with leaving your home unoccupied.
                        </li>
                    </ul>


                    <p className={css.subtitle}>
                        Benefits for House Sitters
                    </p>
                    <ul className={css.list}>
                        <li>
                            Free Accommodation and Travel Opportunities: House sitters enjoy the perk of rent-free stays, allowing them to explore new places affordably or save on temporary relocations.
                        </li>
                        <li>
                            Pet Companionship: For animal lovers, house sitting offers the joy of bonding with pets while on the move.
                        </li>
                        <li>
                            Flexible Arrangements: House sits can range from short weekends to months-long stays, catering to various schedules and preferences.
                        </li>
                    </ul>

                    <H3>
                        How Does House Sitting Work?
                    </H3>
                    <p className={css.info}>
                        House sitting is a straightforward process built on trust and mutual benefit. While each arrangement may vary slightly, the overall process can be broken down into a few simple steps to ensure clarity and ease for both parties.
                    </p>
                    <div className={css.imageContainerHorizontal}>
                        <img className={css.image} src={HiringPetsittersImage} alt="home and pet owners meet the sitter at home" />
                        <p className={css.info}>
                            Home and pet owners meeting the housesitter in their home
                        </p>
                    </div>

                    <p className={css.subtitle}>
                        Steps to Arrange a House Sit
                    </p>
                    <ul className={css.list}>
                        <li>
                            Join a House-Sitting Platform: Both homeowners and sitters begin by registering on a trusted platform like HouseSit Match. Owners create profiles describing their home, pets, and expectations, while sitters highlight their skills, experience, and availability.
                        </li>
                        <li>
                            Find the Right Match: Homeowners list their house-sitting opportunities, and sitters browse these postings to find a suitable match. Once a potential fit is identified, either party can initiate contact to discuss the details.
                        </li>
                        <li>
                            Discuss Responsibilities and Expectations: Clear communication is key. Homeowners outline tasks such as pet care, home maintenance, or garden upkeep, while sitters confirm their ability to meet these expectations. This step ensures mutual understanding and avoids surprises.

                        </li>
                        <li>
                            Agree on Terms and Finalise Details: After aligning on responsibilities, both parties confirm the arrangement. This can include drafting a simple agreement outlining key details, like house rules, emergency contacts, and any specific instructions.
                        </li>
                        <li>
                            Start the House Sit: On the agreed date, the sitter arrives and begins their responsibilities, keeping in touch with the homeowner as needed. At the end of the arrangement, both parties can share feedback to maintain transparency and trust within the community.
                        </li>
                    </ul>
                    <p className={css.info}>
                        By following these steps, house sitting remains a simple and rewarding process for both homeowners and sitters, creating a positive experience from start to finish.
                    </p>

                    <H3>
                        Types of House Sitting Arrangements
                    </H3>

                    <p className={css.info}>
                        From short-term gigs to long-term commitments, the types of house-sitting opportunities available can vary widely depending on the location, duration, and responsibilities involved. Understanding these categories helps homeowners and sitters align their preferences and expectations. Here are some common types of house-sitting arrangements:
                    </p>
                    <ul className={css.list}>
                        <li>
                            Pet Sitting Focused: These assignments primarily involve caring for pets, including feeding, walking, grooming, and providing companionship. They are ideal for animal lovers and often include caring for dogs, cats, birds, or even exotic pets.
                        </li>
                        <li>
                            Plant and Garden Care: Homeowners with extensive gardens or indoor plants often seek sitters to handle watering, pruning, and general upkeep, ensuring their greenery thrives in their absence.
                        </li>
                        <li>
                            Vacation or Holiday House Sitting: These are typically short-term arrangements where owners require sitters during holidays or vacations. Responsibilities may include basic home maintenance and light pet care.
                        </li>
                        <li>
                            Long-Term House Sitting: For extended absences, such as work assignments abroad or extended travel, owners may need sitters to manage their property for months at a time. This arrangement often requires a more detailed agreement and regular communication.
                        </li>
                        <li>
                            Rural or Farm Sitting: Sitters care for homes in remote or rural areas, often with additional responsibilities like tending to farm animals, maintaining large properties, or handling small repairs.
                        </li>
                        <li>
                            Luxury or High-Security Properties: Some arrangements involve high-value properties where sitters are entrusted with added security measures. These roles often require background checks or specific references.
                        </li>
                        <li>
                            City Apartment Sitting: These assignments typically focus on urban living spaces and may involve light care duties for plants or pets, offering sitters the opportunity to explore city life without accommodation costs.
                        </li>
                    </ul>

                    <H3>
                        What House Sitting Is Not
                    </H3>
                    <p className={css.info}>
                        While house sitting shares some similarities with other arrangements, it’s important to understand its unique nature.
                    </p>
                    <p className={css.info}>
                        Unlike a house swap, where two homeowners exchange homes for a set period, house sitting involves one party providing a service in exchange for accommodation. It’s also not a full-time job; it’s typically an informal arrangement that’s flexible and tailored to both parties’ needs.
                    </p>
                    <p className={css.info}>
                        By clarifying what house sitting is and isn’t, both homeowners and sitters can enter the arrangement with realistic expectations.
                    </p>

                    <H3>
                        Tips for Successful House Sitting
                    </H3>
                    <p className={css.info}>
                        Ensuring a smooth and enjoyable house-sitting experience requires preparation and clear communication. Here are some essential tips for both homeowners and house sitters:
                    </p>
                    <p className={css.subtitle}>
                        For Homeowners
                    </p>

                    <ul className={css.list}>
                        <li>Provide clear instructions about house rules, pet care, and maintenance tasks.</li>
                        <li>Share emergency contacts and any local service providers (e.g., vets, gardeners).</li>
                        <li>Create a welcoming environment with essentials like fresh linens and supplies.</li>
                        <li>Arrange a walkthrough before you leave to address any last-minute questions.</li>
                    </ul>

                    <p className={css.subtitle}>
                        For House Sitters
                    </p>
                    <ul className={css.list}>
                        <li>Communicate regularly with the homeowner during the assignment.</li>
                        <li>Follow instructions diligently, especially regarding pet care or home security.</li>
                        <li>Treat the property with respect and maintain its cleanliness.</li>
                        <li>Prepare for contingencies by keeping emergency contacts and instructions handy.</li>
                        <li>By keeping these tips in mind, both parties can foster trust and ensure a positive house-sitting experience, setting the foundation for successful arrangements in the future.</li>
                    </ul>

                    <div className={css.imageContainerVertical}>
                        <img className={css.image} src={BriefingImage} alt="Prepare a full briefing on the pet care and feeding" />
                        <p className={css.info}>
                            Prepare a full briefing on the pet care and feeding
                        </p>
                    </div>

                    <H3>
                        Why Choose HouseSit Match for House Sitting?
                    </H3>

                    <p className={css.info}>
                        HouseSit Match stands out as a reliable platform for connecting homeowners and sitters. With verified profiles and a wide range of listings, it provides a safe and supportive environment for users to find their perfect match.
                    </p>
                    <p className={css.info}>
                        Our platform’s user-friendly features and emphasis on trust make it an ideal choice for those new to house sitting as well as seasoned participants. By joining HouseSit Match, you gain access to a community that values care, responsibility, and mutual benefit.
                    </p>

                    <H3>
                        Frequently Asked Questions About House Sitting
                    </H3>

                    <CollapseFAQ
                        list={defaultFaq}
                    />

                    <p className={css.subtitle}>
                        Start Your House Sitting Journey Today
                    </p>
                    <p className={css.info}>
                        House sitting is more than just a practical arrangement—it’s an opportunity to create meaningful connections, enjoy unique travel experiences, and provide invaluable care for homes and pets.
                    </p>
                    <p className={css.info}>
                        Whether you're a homeowner seeking peace of mind or a sitter looking for your next adventure, house sitting offers unmatched benefits.{" "}
                        <NamedLink name="SignupPage">Register with HouseSit Match</NamedLink>
                        {" "}today and discover how house sitting can work for you.
                    </p>
                </div>
            </LayoutSingleColumn>
        </Page>
    );
};

export default WhatIsHouseSittingPage;
